import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { NavigationStart, Params, Router } from '@angular/router';
import { PortalTypeEnum } from '@ats/api/cm';
import { AtsLoggerService } from '@ats/logger';
import { AtsStoreService } from '@ats/store';
import { AtsDate, AtsDateUnit } from '@ats/utility';
import { CmEventService } from '@cm/core/system';
import { Apollo } from 'apollo-angular';
import {
	BehaviorSubject,
	combineLatest,
	interval,
	Observable,
	of,
	ReplaySubject,
	throwError,
} from 'rxjs';
import {
	catchError,
	distinctUntilChanged,
	filter,
	first,
	map,
	retry,
	switchMap,
	take,
	tap,
} from 'rxjs/operators';
import {
	AccessTokenCreateRequest,
	AppUser,
	AppUserType,
	CmAccessToken,
	CmInitialAccessToken,
	CmInterceptHeaders,
	Guard,
} from '../models';
import { CmSettingsService } from './settings.service';
import { CmTimeoutService } from './timeout.service';

export const TWO_STEP_STORE = 'CM-Two-Step';
const TWO_STEP_STORE_EXPIRE = 'CM-Two-Step-Expire';
const AUTH_COOOKIE = 'authData';
const BANNED_CANDIDATE_ERROR = '5f36d23ba1d545de9659c7f46e080b4c';

export type CmPublicTokenData = Pick<
	CmAccessToken,
	| 'isImpersonating'
	| 'localizationEnabled'
	| 'logoutUrl'
	| 'absoluteExpirationMinutesRemaining'
	| 'slidingExpirationMinutes'
>;

/**
 * Saves an access token to the cache (in a store)
 * @class CmAuthService
 * @property {Observable<AppUser | null>} user$ - An observable that contains the currently logged in user, or null if there is none
 * @property {Observable<boolean>} authenticated$ - An observable derived from user$ mapped to the authenticated property
 * @property {Observable<boolean>} canLocalize$ - An observable derived from user$ mapped to the canLocalize property
 * @property {Observable<boolean>} impersonating$ - An observable derived from user$ mapped to the impersonating property
 */
@Injectable({
	providedIn: 'root',
})
export class CmAuthService {
	private tokenName!: string;
	private twoStepTokenName!: string;
	private twoStepExpiryTokenName!: string;
	private tokenSnapshot: string;
	private tokenSubj$ = new ReplaySubject<CmPublicTokenData | null>(1);
	private guardUserSubj$: BehaviorSubject<AppUser | null> = new BehaviorSubject<AppUser | null>(
		null
	);

	public token$: Observable<CmPublicTokenData | null> = this.tokenSubj$.asObservable();
	public guard$: Observable<Guard>;

	constructor(
		private storage: AtsStoreService,
		private timeoutService: CmTimeoutService,
		private logger: AtsLoggerService,
		private http: HttpClient,
		private apollo: Apollo,
		private settings: CmSettingsService,
		private router: Router,
		private dialogs: MatDialog,
		private events: CmEventService
	) {
		const routeShouldDeleteCurrentToken$ = this.router.events.pipe(
			filter(event => event instanceof NavigationStart),
			take(1),
			map(
				(event: NavigationStart) =>
					this.settings.portal === PortalTypeEnum.CandidatePortal &&
					event.url.includes('impersonate')
			)
		);

		combineLatest([
			this.settings.client$.pipe(filter(client => !!client)),
			routeShouldDeleteCurrentToken$,
		]).subscribe(([client, routeShouldDeleteCurrentToken]) => {
			this.tokenName = `CM-${AUTH_COOOKIE}.${client}`;
			this.twoStepTokenName = `${TWO_STEP_STORE}.${client}`;
			this.twoStepExpiryTokenName = `${TWO_STEP_STORE_EXPIRE}.${client}`;

			if (routeShouldDeleteCurrentToken) {
				this.clearAuthToken();
			}

			const token = this.getAuthToken();

			if (token) {
				this.tokenSnapshot = token;
				this.validateToken$().pipe(take(1)).subscribe();
			} else {
				this.updatePublicToken(null);
			}
		});

		this.monitorCookieValidity$();

		this.guard$ = Guard.getFromUser$(this.guardUserSubj$.asObservable());
	}

	/**
	 * Get a guard instance with the current user information
	 * @method
	 * @return {Guard} - Returns an instance of a guard using the current user
	 */
	public guard(): Guard {
		const user = this.guardUserSubj$.getValue();
		if (!user) {
			const errMessage = 'Guard cannot be invoked before user is loaded';
			this.logger.critical(errMessage);
			throw new Error(errMessage);
		}
		return new Guard(Guard.getCheckpointsFromUser(user));
	}

	/**
	 * Generate a token by logging someone in
	 * @method
	 * @param {AccessTokenCreateRequest} request - The request credentials for the entity logging in
	 * @return {Observable<boolean>} - Emits true if succesfully logging the person in
	 */
	login$(request: AccessTokenCreateRequest): Observable<boolean> {
		return this.updateTokenWithPost$(`${this.settings.api}/access-token`, request).pipe(
			map(Boolean),
			catchError(err => {
				if (this.isBannedCandidateError(err)) {
					return this.handleBannedCandidateError();
				}
				return throwError(() => err);
			})
		);
	}

	/**
	 * Invalidates the current session
	 * @method
	 * @return {Observable<boolean>} - Emits true if the logout was successful
	 */
	logout$(): void {
		if (this.getAuthToken()) {
			const headers = new HttpHeaders({
				...CmInterceptHeaders.IgnoreAuthError,
			});
			this.token$
				.pipe(
					first(),
					switchMap(token =>
						this.http.delete(`${this.settings.api}/access-token`, { headers }).pipe(
							catchError(this.handleAPIError),
							map(() => (token || {}).logoutUrl || null)
						)
					),
					tap(() => this.clearAuthCache())
				)
				.subscribe(url => {
					if (url) {
						window.location.href = url;
					} else {
						this.router.navigate(this.settings.route(['login']));
					}
				});
		} else {
			this.clearAuthCache();
		}
	}

	/**
	 * Public method to update the user used for the guard
	 * @method
	 * @param {AppUser | null} user - The user to use for the guard
	 * @return {void}
	 */

	updateGuardUser(user: AppUser | null): void {
		this.guardUserSubj$.next(user);
	}

	/**
	 * Generate a token via impersonation
	 * @method
	 * @param {string} url - The url to get the token from
	 * @return {Observable<boolean>} - Emits true if succesfully getting and validating the token
	 */
	updateTokenWithPost$(url: string, request: any = {}, options?: any): Observable<boolean> {
		return this.generateToken$(url, options, request).pipe(map(Boolean));
	}

	/**
	 * Generate a token via impersonation
	 * @method
	 * @param {string} url - The url to get the token from
	 * @return {Observable<boolean>} - Emits true if succesfully getting and validating the token
	 */
	updateTokenWithGet$(url: string, options?: any): Observable<boolean> {
		return this.generateToken$(url, options).pipe(
			map(Boolean),
			catchError(err => {
				if (this.isBannedCandidateError(err)) {
					return this.handleBannedCandidateError();
				}
				return throwError(() => err);
			})
		);
	}

	/**
	 * Hits the API to generate an access token, then validates the token
	 * in order to check if two-step is required and get all of the token data
	 * @method
	 * @private
	 * @param {string} token - The access token value
	 * @param {number} expiry - The store expiration in number of minutes
	 * @return {CmAccessToken | null} - The access token, or null if it wasn't valid
	 */
	private generateToken$(
		url: string,
		options: any = {},
		request: any = null
	): Observable<boolean | null> {
		const apiCall$ = !request
			? this.http.get<CmInitialAccessToken>(url, options)
			: this.http.post<CmInitialAccessToken>(url, request, options);
		return apiCall$.pipe(
			tap(this.handleTokenSideEffects),
			switchMap((response: CmInitialAccessToken | null) =>
				!response ? of(null) : this.validateToken$()
			)
		);
	}

	/**
	 * Validates the access token currently stored in the store
	 * @method
	 * @return {CmAccessToken | null} - The access token, or null if not valid. Errors are logged to the API.
	 */
	validateToken$(): Observable<boolean> {
		return this.http
			.get<CmAccessToken>(`${this.settings.api}/access-token`, {
				headers: CmInterceptHeaders.IgnoreAuthError,
			})
			.pipe(
				retry(1),
				catchError(err => {
					this.updatePublicToken(null);
					return throwError(err);
				}),
				tap(response => {
					this.updatePublicToken(response || null);
				}),
				map(Boolean)
			);
	}

	/**
	 * Ensures that currently stored token is in a valid state, i.e. it hasn't changed in other tab
	 * Does not test the validity of the token value itself.
	 * @method
	 * @return {boolean} - True if there is no token snapshot or thee token matches the snapshot
	 */
	validateTokenAgainstSnapshot(): boolean {
		if (!this.tokenSnapshot) {
			return true;
		}
		const token = this.getAuthToken();
		return token && this.tokenSnapshot === token;
	}

	/**
	 * Triggers the email for the two step login process
	 * @method
	 * @return {Observable<boolean>} - An observable that emits true once the email has successfully be seent
	 */
	triggerTwoStepEmail(isIp: boolean = false): Observable<boolean> {
		return this.settings.culture$.pipe(
			take(1),
			switchMap(culture =>
				this.http.post(
					isIp
						? `${this.settings.api}/two-step/en?ip=true`
						: `${this.settings.api}/two-step/${culture.code}`,
					{},
					{
						headers: CmInterceptHeaders.IgnoreAuthError,
					}
				)
			),
			catchError(this.handleAPIError),
			map(Boolean)
		);
	}

	/**
	 * Validates the query param token during the two-factor process
	 * @method
	 * @param {string} token - The token that needs validated
	 * @return {Observable<boolean>} - An observable that emits true once the email has successfully be seent
	 */
	validateTwoStepToken(token: string): Observable<boolean> {
		// Make sure the token is resolved before we start this process
		return this.settings.culture$.pipe(
			take(1),
			switchMap(culture =>
				this.http.get<{ token: string; absoluteExpiration: string }>(
					`${this.settings.api}/two-step/${culture.code}?t=${token}`
				)
			),
			catchError(this.handleAPIError),
			switchMap(response => {
				if (!response) {
					return throwError('Unable to validate two step token');
				}
				const storeExpiration = AtsDate.until(
					new Date(response.absoluteExpiration),
					AtsDateUnit.Minute
				);
				this.storage.saveToStore(this.twoStepTokenName, response.token, storeExpiration);
				this.storage.saveToStore(
					this.twoStepExpiryTokenName,
					response.absoluteExpiration,
					storeExpiration
				);
				return this.validateToken$();
			}),
			map(Boolean)
		);
	}

	/**
	 * Saves an access token to the cache (in a store)
	 * @method
	 * @param {string} token - The access token value
	 * @param {number} expiry - The store expiration in number of minutes
	 * @return {void}
	 */
	setAuthToken(token: string, expiry: number): void {
		if (!this.tokenName) {
			const errMessage = 'Token cannot be set before client has been resolved';
			this.logger.critical(errMessage);
			return;
		}
		this.storage.saveToStore(this.tokenName, token, expiry);
	}

	/**
	 * Clears out the current auth token store
	 * @method
	 * @return {void} - An observable that emits true if the user/candidate was successfully logged in
	 */
	clearAuthToken(): void {
		this.storage.deleteFromStore(this.tokenName);
		this.tokenSnapshot = null;
	}

	/**
	 * Clears out the current public token store
	 * @method
	 * @return {void}
	 */
	clearPublicToken(): void {
		this.updatePublicToken(null);
	}

	/**
	 * Gets the current access token stored in the cache (as a store)
	 * @method
	 * @return {string | null} - The value of the access token in the store, or null if not found
	 */
	getAuthToken(): string | null {
		if (!this.tokenName) {
			return null;
		}
		return this.storage.getFromStore(this.tokenName) || null;
	}

	/**
	 * Gets the current two-step token stored in the cache (as a store)
	 * @method
	 * @return {string | null} - The value of the access token in the store, or null if not found
	 */
	getTwoStepToken(): string | null {
		if (!this.twoStepTokenName) {
			return null;
		}
		return this.storage.getFromStore(this.twoStepTokenName) || null;
	}

	/**
	 * Resets the apollo cache
	 * Clears it out and re-runs all active queries
	 * @method
	 * @return {void}
	 */
	resetApolloCache(): Promise<void> {
		return this.apollo.client.cache.reset();
	}

	/**
	 * Converts a URL that is a string into values the createUrlTree function on Angular's
	 * Router can consume. This does not remove client or culture.
	 * Examples of URLs
	 * 1. '/home/dashboard'
	 * 2. '/sso/vue?autoRedirect=true'
	 * @method
	 * @param {string} url - URL that needs converting
	 * @return {{path: string[]; queryParams: Params }}
	 */
	convertUrlToUrlTree(url: string): { path: string[]; queryParams: Params } {
		const hasQueryParams = url.includes('?');
		const redirectUrlWithoutParams = hasQueryParams ? url.slice(0, url.indexOf('?')) : url;
		const queryParams = {};
		if (hasQueryParams) {
			url.slice(url.indexOf('?') + 1)
				.split('&')
				.forEach(queryParamAndValue => {
					const [param, value] = queryParamAndValue.split('=');
					queryParams[param] = value;
				});
		}
		return {
			path: [...redirectUrlWithoutParams.split('/').filter(segment => !!segment)],
			queryParams,
		};
	}

	/**
	 * Broadcasts an error to the event service if the candidate is banned
	 * @private
	 * @method
	 * @return {Observable<false>}
	 */

	private isBannedCandidateError = (err: any): boolean => {
		const id = err.id || err?.error?.id;
		const status = err.statusCode || err?.error?.statusCode;
		if (status === 403 && id === BANNED_CANDIDATE_ERROR) {
			this.events.handleAuthError(BANNED_CANDIDATE_ERROR, 'ban-redirect');
			return true;
		}
		return false;
	};

	private handleBannedCandidateError = (): Observable<never> =>
		throwError(() => new Error('banned-error'));

	/**
	 * Updates public token subject
	 * @private
	 * @method
	 * @return {void}
	 */
	private updatePublicToken(response: CmAccessToken | null): void {
		if (!response) {
			this.tokenSubj$.next(null);
		} else {
			// User type on token must match the portal we are in
			if (
				(this.settings.portal === PortalTypeEnum.CandidatePortal &&
					response.accessType === AppUserType.CANDIDATE) ||
				(this.settings.portal !== PortalTypeEnum.CandidatePortal &&
					response.accessType !== AppUserType.CANDIDATE)
			) {
				this.tokenSubj$.next({
					isImpersonating: response.isImpersonating,
					localizationEnabled: response.localizationEnabled,
					logoutUrl: response.logoutUrl,
					absoluteExpirationMinutesRemaining: response.absoluteExpirationMinutesRemaining,
					slidingExpirationMinutes: response.slidingExpirationMinutes,
				});
			} else {
				this.tokenSubj$.next(null);
			}
		}
	}

	/**
	 * Monitors the validity of the current token state
	 * NOT the validity of the token itself
	 * @private
	 * @method
	 * @return {void}
	 */
	private monitorCookieValidity$(): void {
		// This is the validity of the state of the token/snapshot matching
		// NOT the validity of the token value
		interval(500)
			.pipe(
				map(() => {
					const token = this.getAuthToken();
					// Not having a token -- VALID STATE
					// OR
					// Have a token and snapshot is empty --- VALID STATE
					// OR
					// Have a token and a snapshot and they are equal --- VALID STATE
					const validTokenState =
						(!token && !this.tokenSnapshot) ||
						(token && !this.tokenSnapshot) ||
						(token && this.tokenSnapshot && this.tokenSnapshot === token);

					// Token snapshot needs to go away if the token is gone OR they don't match
					if (!token || this.tokenSnapshot !== token) {
						this.tokenSnapshot = null;
					}
					return validTokenState;
				}),
				distinctUntilChanged()
			)
			.subscribe(validTokenState => {
				if (!validTokenState) {
					this.updatePublicToken(null);
					this.router.navigate(this.settings.route(['session-expired']));
				}
			});
	}

	/**
	 * Clears out authed cache data
	 * @private
	 * @method
	 * @return {void}
	 */
	private clearAuthCache(): void {
		this.dialogs.closeAll();
		this.clearAuthToken();
		this.updatePublicToken(null);
		this.apollo.client.stop();
		this.apollo.client.clearStore();
		this.timeoutService.deleteExpiration();
	}

	/**
	 * Grabs the access token snapshot and saves it to a store
	 * @private
	 * @method
	 * @param {CmInitialAccessToken | null} response - An initial access token response
	 * @return {void}
	 */
	private handleTokenSideEffects = (response: any) => {
		if ((response || {}).accessTokenId) {
			this.tokenSnapshot = response.accessTokenId;
			this.setAuthToken(response.accessTokenId, response.absoluteExpirationMinutes || 480);
			this.timeoutService.setTimeoutToStorage(response);
		}
	};

	private handleAPIError = (err: any): Observable<false> => {
		return of(false);
	};
}
